import * as React from "react";
import { track } from "@/lib/track";

const KEY = "cd_session_started_at";
const WINDOW_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Marks the start of a 30-minute browser session. Fires `session_started`
 * once per window per tab/profile. Subsequent calls within 30min of the
 * last activity are no-ops; the marker rolls forward on each call so an
 * active user keeps the same session.
 *
 * Called from <AuthProvider> after we observe an authenticated user.
 */
export function useSession(authedUserId: string | null) {
  React.useEffect(() => {
    if (!authedUserId) return;
    if (typeof window === "undefined") return;
    try {
      const raw = window.sessionStorage.getItem(KEY);
      const now = Date.now();
      const last = raw ? Number(raw) : 0;
      if (!last || Number.isNaN(last) || now - last > WINDOW_MS) {
        track("session_started", { boundary_minutes: 30 });
      }
      window.sessionStorage.setItem(KEY, String(now));
    } catch {
      // sessionStorage can throw in private mode — analytics must not break UI.
    }
  }, [authedUserId]);
}

/** Clear the session marker so the next sign-in emits a fresh session_started. */
export function clearSessionMarker() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}